import { StandardEvents } from '@shopify/events';

/**
 * KWC · collection grid + product card behaviour
 * ---------------------------------------------------------------------------
 * Loaded on collection pages and product pages (layout/theme.liquid).
 *
 * 1. Load more (snippets/kwc-load-more.liquid). The button is a link to the
 *    next page. Here it fetches that page through the Section Rendering API,
 *    appends its cards to the grid, updates "Showing X of Y" and the link, and
 *    sets ?page= with history.replaceState. If anything fails it follows the
 *    link instead, so the next page still opens. Clicks are delegated, so it
 *    keeps working after a filter or sort re-renders the grid.
 * 2. Add to cart on a product card opens the cart drawer once the add has
 *    gone through. The theme's "Open cart drawer on add" setting is off, so
 *    this applies to cards only; the product page's own Add to cart keeps its
 *    current behaviour.
 * 3. The grid-density toggle is gone, so a grid view a visitor saved earlier
 *    (sessionStorage) is cleared and the grid shows its default layout.
 */

/* --- 1. Load more --------------------------------------------------------- */
const countText = (first, last, total) =>
  `Showing ${first > 1 ? `${first}–${last}` : last} of ${total}`;

document.addEventListener('click', async (event) => {
  const link = event.target instanceof Element ? event.target.closest('[data-kwc-load-more-button]') : null;
  if (!(link instanceof HTMLAnchorElement)) return;
  // Opening in a new tab or window stays a normal link.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

  const block = link.closest('[data-kwc-load-more]');
  const grid = block?.parentElement?.querySelector(':scope > ul.product-grid');
  if (!(block instanceof HTMLElement) || !grid) return;

  event.preventDefault();
  if (link.getAttribute('aria-busy') === 'true') return;
  link.setAttribute('aria-busy', 'true');

  const pageUrl = new URL(link.href, window.location.href);
  try {
    const fetchUrl = new URL(pageUrl);
    fetchUrl.searchParams.set('section_id', block.dataset.sectionId || '');
    const response = await fetch(fetchUrl, { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const items = [...html.querySelectorAll('ul.product-grid > li.product-grid__item')];
    const nextBlock = html.querySelector('[data-kwc-load-more]');
    if (!items.length || !(nextBlock instanceof HTMLElement)) throw new Error('no cards in the next page');

    const added = items.map((item) => document.importNode(item, true));
    grid.append(...added);

    const last = Number(nextBlock.dataset.last) || Number(block.dataset.last);
    block.dataset.last = String(last);
    const count = block.querySelector('[data-kwc-load-more-count]');
    if (count) count.textContent = countText(Number(block.dataset.first) || 1, last, Number(block.dataset.total) || last);

    const nextLink = nextBlock.querySelector('[data-kwc-load-more-button]');
    if (nextLink instanceof HTMLAnchorElement) {
      link.href = nextLink.href;
    } else {
      link.remove();
    }

    history.replaceState(history.state, '', pageUrl);

    // Keyboard and screen-reader users continue from the first new watch.
    added[0]?.querySelector('a[href]')?.focus({ preventScroll: true });
  } catch (error) {
    console.warn('[kwc-collection] Load more fell back to the next page:', error);
    window.location.href = pageUrl.href;
  } finally {
    link.removeAttribute('aria-busy');
  }
});

/* --- 2. Card Add to cart opens the drawer -------------------------------- */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const lines = /** @type {any} */ (event);
  if (lines.action !== 'add') return;
  const fromCard = event.target instanceof Element && event.target.closest('product-card');
  if (!fromCard) return;

  Promise.resolve(lines.promise)
    .then((result) => {
      if (result?.detail?.didError) return;
      const drawer = /** @type {any} */ (document.querySelector('#cart-drawer'));
      if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
    })
    .catch(() => {});
});

/* --- 3. Default grid view ------------------------------------------------- */
try {
  sessionStorage.removeItem('product-grid-view-mobile');
  sessionStorage.removeItem('product-grid-view-desktop');
} catch (error) {
  // Storage can be unavailable (private mode); the grid then has no saved view anyway.
}
document.querySelectorAll('main ul.product-grid[product-grid-view]').forEach((grid) => {
  if (grid.getAttribute('product-grid-view') !== 'default') grid.setAttribute('product-grid-view', 'default');
});
